/**
 * Turns the signed-in player's avatar into a PNG and hands it to the platform share sheet (or
 * downloads it when sharing files is not available). Rendering and sharing are split into small,
 * injectable pieces so each can be tested without a real canvas or `navigator.share`.
 */
import type { Avatar } from '../models/types';
import { STACK_ORDER } from './AvatarView';
import { avatarBodyFor } from './bodies';
import { type AvatarBox, type WornLayer, resolveWornLayer } from './itemArt';

/** One image to draw on the canvas, back to front: the base body first, then each worn layer. */
export interface ImageLayer {
  url: string;
  box: AvatarBox;
}

/** The body plus every equipped item's worn layer, in draw order. Pure: no canvas, no network. */
export function avatarImageLayers(avatar: Avatar, resolveWorn: (itemId: string) => WornLayer | null = resolveWornLayer): ImageLayer[] {
  const body = avatarBodyFor(avatar.body);
  const layers: ImageLayer[] = [{ url: body.image, box: [0, 0, 1, 1] }];
  for (const slot of STACK_ORDER) {
    const itemId = avatar.equipped[slot];
    const layer = itemId ? resolveWorn(itemId) : null;
    if (layer) layers.push({ url: layer.url, box: layer.box });
  }
  return layers;
}

/** Something `drawImage` can draw; an `HTMLImageElement` in production, a test double in tests. */
export type DrawableImage = unknown;

/** The slice of a 2D canvas context this module needs. */
export interface DrawContext {
  drawImage(image: DrawableImage, x: number, y: number, w: number, h: number): void;
}

/** The slice of a canvas this module needs: a 2D context and a way to turn it into a PNG blob. */
export interface AvatarCanvas {
  getContext(kind: '2d'): DrawContext | null;
  toBlob(callback: (blob: Blob | null) => void, type?: string): void;
}

function defaultLoadImage(url: string): Promise<DrawableImage> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Could not load ${url}`));
    img.src = url;
  });
}

function defaultCreateCanvas(size: number): AvatarCanvas {
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  return canvas as unknown as AvatarCanvas;
}

/** The side of the exported PNG, in pixels. */
export const SHARE_IMAGE_SIZE = 640;

/**
 * Draws the avatar's layers onto a square canvas and resolves with a PNG blob. `loadImage` and
 * `createCanvas` are injectable so the compositing logic is testable without a real canvas.
 */
export async function renderAvatarPng(
  avatar: Avatar,
  size: number = SHARE_IMAGE_SIZE,
  resolveWorn: (itemId: string) => WornLayer | null = resolveWornLayer,
  loadImage: (url: string) => Promise<DrawableImage> = defaultLoadImage,
  createCanvas: (size: number) => AvatarCanvas = defaultCreateCanvas,
): Promise<Blob> {
  const layers = avatarImageLayers(avatar, resolveWorn);
  const images = await Promise.all(layers.map((layer) => loadImage(layer.url)));
  const canvas = createCanvas(size);
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas drawing is not supported on this device.');
  layers.forEach((layer, i) => {
    const [x0, y0, x1, y1] = layer.box;
    ctx.drawImage(images[i], x0 * size, y0 * size, (x1 - x0) * size, (y1 - y0) * size);
  });
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('Could not create the avatar image.'))), 'image/png');
  });
}

/** `name` cut down to a safe, lowercase filename fragment; empty or symbol-only names fall back to "hunter". */
function slug(name: string): string {
  const cleaned = name.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  return cleaned || 'hunter';
}

export const avatarShareFilename = (displayName: string): string => `ethrhunt-${slug(displayName)}.png`;

export type ShareOutcome = 'shared' | 'downloaded' | 'cancelled';

/** What `shareAvatarBlob` needs from the platform; `browserShareEnv` supplies the real thing. */
export interface ShareAvatarEnv {
  canShare?: (data: ShareData) => boolean;
  share?: (data: ShareData) => Promise<void>;
  saveAs: (blob: Blob, filename: string) => void;
}

const SHARE_TITLE = 'My EthrHunt look';
const SHARE_TEXT = 'Check out my look in EthrHunt! 🎒✨';

/**
 * Shares the rendered PNG through the platform share sheet when it can carry a file, otherwise
 * falls back to `env.saveAs`. The player cancelling the native share sheet is not an error.
 */
export async function shareAvatarBlob(blob: Blob, displayName: string, env: ShareAvatarEnv): Promise<ShareOutcome> {
  const filename = avatarShareFilename(displayName);
  if (env.share && env.canShare) {
    const file = new File([blob], filename, { type: 'image/png' });
    if (env.canShare({ files: [file] })) {
      try {
        await env.share({ files: [file], title: SHARE_TITLE, text: SHARE_TEXT });
        return 'shared';
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') return 'cancelled';
        throw err;
      }
    }
  }
  env.saveAs(blob, filename);
  return 'downloaded';
}

/** Downloads a blob through a throwaway link, the usual trick for saving a generated file. */
function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** The real platform environment: `navigator.share`/`canShare` when present, else a download. */
export function browserShareEnv(): ShareAvatarEnv {
  const nav = navigator;
  return {
    canShare: typeof nav.canShare === 'function' ? nav.canShare.bind(nav) : undefined,
    share: typeof nav.share === 'function' ? nav.share.bind(nav) : undefined,
    saveAs: downloadBlob,
  };
}

/** Renders the player's avatar and shares (or downloads) it. The entry point the You tab calls. */
export async function shareAvatarPicture(
  avatar: Avatar,
  displayName: string,
  render: (avatar: Avatar) => Promise<Blob> = renderAvatarPng,
  env: ShareAvatarEnv = browserShareEnv(),
): Promise<ShareOutcome> {
  const blob = await render(avatar);
  return shareAvatarBlob(blob, displayName, env);
}
